import type { JSX } from 'react';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { InlineMessage } from '@change-corgi/design-system-legacy/components/alerts';
import { Select } from '@change-corgi/design-system-legacy/components/forms';
import { Box } from '@change-corgi/design-system-legacy/layout';
import { Heading } from '@change-corgi/design-system-legacy/typography';

import { EXPORT_OPTIONS } from './constants';
import { useExportPetitionForm } from './hooks/useExportPetitionForm';

export function ExportPetitionForm(): JSX.Element | null {
	const { data, actions } = useExportPetitionForm();
	const { translate } = useI18n();

	if (data.shouldHideExport) return null;

	return (
		<Box>
			<Box p="16px 24px 24px">
				<form onSubmit={actions.handleExport}>
					<Heading as="h3" size="h4" id="petition-export-label">
						<Translate value="corgi.petition.starter_dashboard.engage_your_supporters.recent_supporters.subtitle" />
					</Heading>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: '1fr max-content',
							alignItems: 'flex-start',
							width: '100%',
							mt: 16,
							// eslint-disable-next-line @typescript-eslint/naming-convention
							'& > #export-petition-type___error': { gridRow: 2, gridColumn: '1/-1', mt: 8 },
						}}
					>
						<Select
							id="export-petition-type"
							name="export-petition-type"
							aria-labelledby="petition-export-label"
							error={!data.loading && data.errorMessage && <Translate value={data.errorMessage} />}
							sx={{ height: 44 }}
						>
							{EXPORT_OPTIONS.map((exportOption) => (
								<option key={exportOption.id} value={exportOption.value}>
									{translate(exportOption.labelKey)}
								</option>
							))}
						</Select>

						<Button
							data-testid="export-get-list-button"
							data-qa="export-get-list-button"
							type="submit"
							size="medium"
							variant="secondary"
							sx={{ flexShrink: 0 }}
							disabled={data.loading}
							loading={data.loading}
							ml={16}
						>
							<Translate value="corgi.petition.starter_dashboard.engage_your_supporters.recent_supporters.get_list_button" />
						</Button>
					</Box>

					<Box aria-atomic="true" aria-live="polite">
						{data.successMessage ? (
							<InlineMessage title="" variant="success" sx={{ mt: 20 }} id="success-message">
								<Translate value={data.successMessage} />
							</InlineMessage>
						) : null}
					</Box>
				</form>
			</Box>
		</Box>
	);
}
